import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, DollarSign, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MyPayroll = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/me/payslips');
        setPayslips(res.data);
        if (res.data.length > 0) setSelected(res.data[0]);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const downloadPdf = async (p) => {
    try {
      const res = await api.get(`/payslips/${p.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `payslip_${p.year}_${p.month}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  return (
    <div className="space-y-6" data-testid="my-payroll-page">
      <div>
        <h1 className="text-2xl font-bold">My Payroll</h1>
        <p className="text-muted-foreground">View your salary breakdown and payslip history</p>
      </div>

      {selected && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign size={18} className="text-primary" />
                Payslip · {MONTHS[selected.month - 1]} {selected.year}
              </CardTitle>
              <Button onClick={() => downloadPdf(selected)} variant="outline" size="sm" data-testid="download-payslip-btn">
                <Download size={14} className="mr-2" />Download PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-3 bg-emerald-500/10 rounded">
                <p className="text-xs text-muted-foreground">Gross Salary</p>
                <p className="text-xl font-bold text-emerald-600">{selected.currency} {selected.gross_salary?.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-red-500/10 rounded">
                <p className="text-xs text-muted-foreground">Total Deductions</p>
                <p className="text-xl font-bold text-red-600">{selected.currency} {selected.total_deductions?.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-primary/10 rounded">
                <p className="text-xs text-muted-foreground">Net Pay</p>
                <p className="text-xl font-bold text-primary">{selected.currency} {selected.net_salary?.toLocaleString()}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-sm">Earnings</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(selected.earnings || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-sm"><span className="text-muted-foreground">{k}</span><span className="font-mono">{selected.currency} {v?.toLocaleString()}</span></div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-sm">Deductions</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(selected.deductions || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-sm"><span className="text-muted-foreground">{k}</span><span className="font-mono">{selected.currency} {v?.toLocaleString()}</span></div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {selected.employer_contributions && Object.keys(selected.employer_contributions).length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-sm">Employer Contributions</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(selected.employer_contributions).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-sm"><span className="text-muted-foreground">{k}</span><span className="font-mono">{selected.currency} {v?.toLocaleString()}</span></div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-lg">Payslip History</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Gross</TableHead><TableHead>Deductions</TableHead><TableHead>Net</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {payslips.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No payslips yet</TableCell></TableRow>}
              {payslips.map(p => (
                <TableRow key={p.id} className="hover:bg-muted/40" data-testid={`payslip-row-${p.id}`}>
                  <TableCell className="font-medium cursor-pointer" onClick={() => setSelected(p)}>{MONTHS[p.month-1]} {p.year}</TableCell>
                  <TableCell>{p.currency} {p.gross_salary?.toLocaleString()}</TableCell>
                  <TableCell className="text-red-500">{p.currency} {p.total_deductions?.toLocaleString()}</TableCell>
                  <TableCell className="font-bold text-emerald-500">{p.currency} {p.net_salary?.toLocaleString()}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" aria-label="Download payslip PDF" title="Download PDF" onClick={(e) => { e.stopPropagation(); downloadPdf(p); }} data-testid={`download-payslip-${p.id}`}>
                      <Download size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyPayroll;
